// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: menus for the desktop Tasks workspace, on the app's Popout (so Escape,
// outside clicks and stacking behave like every other popover). Items are
// quiet rows with a leading icon and an optional check or hint.

import styles from '@app/features/konvo_tasks/components/workspace/desktop/WorkspaceMenu.module.css';
import {CheckIcon} from '@app/features/ui/icons/Icons';
import type {PopoutPosition} from '@app/features/ui/popover';
import {Popout} from '@app/features/ui/popover/PopoverPopout';
import {clsx} from 'clsx';
import type React from 'react';
import {useEffect, useRef} from 'react';

/** Focus a field once it mounts (menus render into a portal, so wait a frame). */
export function useFocusOnMount<T extends HTMLElement>(enabled = true): React.RefObject<T | null> {
	const ref = useRef<T | null>(null);
	useEffect(() => {
		if (!enabled) return;
		const frame = requestAnimationFrame(() => ref.current?.focus());
		return () => cancelAnimationFrame(frame);
	}, [enabled]);
	return ref;
}

export const menuTokens = styles.tokens;

export function MenuPopout({
	render,
	children,
	position = 'bottom-start',
	wide = false,
	className,
}: {
	render: (close: () => void) => React.ReactNode;
	children: React.ReactElement;
	position?: PopoutPosition;
	wide?: boolean;
	className?: string;
}) {
	return (
		<Popout
			position={position}
			offsetMainAxis={6}
			render={({onClose}) => (
				<div className={clsx(styles.tokens, styles.menu, wide && styles.wide, className)} role="menu">
					{render(onClose)}
				</div>
			)}
		>
			{children}
		</Popout>
	);
}

export function MenuItem({
	icon,
	label,
	hint,
	checked,
	danger,
	disabled,
	onSelect,
	close,
	keepOpen,
	'data-flx': dataFlx,
}: {
	icon?: React.ReactNode;
	label: React.ReactNode;
	hint?: React.ReactNode;
	checked?: boolean;
	danger?: boolean;
	disabled?: boolean;
	onSelect: () => void;
	close?: () => void;
	/** Checkbox-style items stay open so several can be toggled. */
	keepOpen?: boolean;
	'data-flx'?: string;
}) {
	return (
		<button
			type="button"
			{...(checked == null
				? {role: 'menuitem'}
				: {role: keepOpen ? 'menuitemcheckbox' : 'menuitemradio', 'aria-checked': checked})}
			className={clsx(styles.item, danger && styles.itemDanger)}
			disabled={disabled}
			onClick={(event) => {
				event.stopPropagation();
				if (!keepOpen) close?.();
				onSelect();
			}}
			data-flx={dataFlx}
		>
			{icon != null && <span className={styles.itemIcon}>{icon}</span>}
			<span className={styles.itemLabel}>{label}</span>
			{hint != null && <span className={styles.itemHint}>{hint}</span>}
			{checked != null && <span className={styles.itemCheck}>{checked && <CheckIcon size={16} weight="bold" />}</span>}
		</button>
	);
}

export function MenuLabel({children}: {children: React.ReactNode}) {
	return <div className={styles.label}>{children}</div>;
}

export function MenuSeparator() {
	return <hr className={styles.sep} />;
}

export function MenuDot({color}: {color: string | null}) {
	return <span className={styles.dot} style={{background: color ?? '#87909f'}} />;
}

export const menuFieldClass = styles.field;
export const menuFieldRowClass = styles.fieldRow;
